import { useCallback, useEffect, useRef } from 'react';

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

type UseVersionCheckOptions = {
  currentVersion: string;
  onNewVersionDetected: () => void;
  enabled?: boolean;
};

export function useVersionCheck({
  currentVersion,
  onNewVersionDetected,
  enabled = true,
}: UseVersionCheckOptions) {
  const newVersionAvailable = useRef(false);

  const checkVersion = useCallback(async () => {
    if (!enabled || !currentVersion) return;
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.version && data.version !== currentVersion) {
        newVersionAvailable.current = true;
      }
    } catch {
      // Fail silently — never disrupt the user
    }
  }, [enabled, currentVersion]);

  useEffect(() => {
    if (!enabled) return;

    checkVersion();
    const interval = setInterval(checkVersion, POLL_INTERVAL);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, checkVersion]);

  const checkAndReload = useCallback(() => {
    if (newVersionAvailable.current) {
      onNewVersionDetected();
    }
  }, [onNewVersionDetected]);

  return { checkAndReload };
}
